import { Input } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import { Condition } from 'api-clients/monolith';
import { produce } from 'immer';
import React, {
  FC,
  HTMLInputTypeAttribute,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Error } from 'components/Error';
import { getConditionErrorMessage } from 'containers/WorkflowEditor/components/Rules/Condition/constants';
import { messages as conditionMessages } from 'containers/WorkflowEditor/components/Rules/Condition/messages';
import globalMessages from 'shared/global/messages';

import { DocumentSigningRuleProps } from '../../DocumentSigningRules/types';
import {
  PartnerRuleProps,
  SharedPartnerConditionProps,
} from '../../PartnerRules/types';
import { RulesProps, SharedConditionProps } from '../../types';

type FilteredKeys<T, U> = NonNullable<
  { [K in keyof T]: T[K] extends U | undefined ? K : never }[keyof T]
>;

export type ConditionInputKeys = FilteredKeys<
  Condition['extra'],
  string | number
>;

export type InputFieldProps = (
  | SharedConditionProps
  | SharedPartnerConditionProps
) & {
  valueKey?: ConditionInputKeys;
  inputType?: HTMLInputTypeAttribute;
  placeholder?: string;
  required?: boolean;
};

export const InputField: FC<InputFieldProps> = ({
  condition,
  ruleId,
  setRules,
  setPartnerRules,
  setDocumentSigningRules,
  valueKey = 'value',
  errors,
  inputType = 'text',
  placeholder,
  required = true,
}) => {
  const intl = useIntl();
  const { extra } = condition;
  const [inputValue, setInputValue] = useState(extra[valueKey] ?? '');
  // Only the "Is in the list" comparator (`extra.comparator === 'in'`) parses
  // this field's value as a comma-separated list on the backend (see
  // apps/hire/app/models/concerns/data_comparison.rb#cmp_in). Detecting it
  // here centralizes the guidance for every condition type that reuses this
  // shared InputField alongside the full comparator list (ApplicantData,
  // PartnerLabelHasKeyWithValue, PartnerDetailHasKeyWithValue) without
  // affecting condition types that only expose basic comparators.
  const isInTheList = extra.comparator === 'in';
  const { current: helperTextId } = useRef(
    `in-the-list-guidance-${Math.random().toString(36).slice(2)}`,
  );

  useEffect(() => {
    setInputValue(extra[valueKey] ?? '');
  }, [extra, valueKey]);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onBlurInput = () => {
    const rulesSetter = setRules ?? setDocumentSigningRules;

    if (rulesSetter) {
      rulesSetter(
        produce((draftRules: RulesProps | DocumentSigningRuleProps) => {
          const draftConditions =
            draftRules[ruleId]?.condition_set_attributes
              ?.conditions_attributes ?? [];

          const draftCondition = draftConditions.find(
            cond => cond.id === condition.id,
          );
          if (draftCondition) {
            draftCondition.extra = {
              ...draftCondition.extra,
              [valueKey]: inputValue,
            };
          }
        }),
      );
    }

    if (setPartnerRules) {
      setPartnerRules(
        produce((draftRules: PartnerRuleProps) => {
          const draftCondition = draftRules[ruleId].condition_attributes;

          draftCondition.extra = {
            ...draftCondition.extra,
            [valueKey]: inputValue,
          };
        }),
      );
    }
  };

  const errorMessage =
    !extra[valueKey] && getConditionErrorMessage(valueKey, errors);

  // Reuse the existing placeholder plumbing to show an example, but only
  // when the caller hasn't already supplied a more specific placeholder
  // (e.g. the YYYY-MM-DD date format hint).
  const resolvedPlaceholder =
    placeholder ||
    (isInTheList
      ? intl.formatMessage(conditionMessages.isInTheListPlaceholder)
      : placeholder);

  return (
    <>
      <Input
        label={intl.formatMessage(globalMessages.value)}
        aria-label={intl.formatMessage(globalMessages.value)}
        aria-describedby={isInTheList ? helperTextId : undefined}
        fullWidth
        value={inputValue}
        onChange={onChangeInput}
        onBlur={onBlurInput}
        type={inputType}
        placeholder={resolvedPlaceholder}
        error={Boolean(errorMessage)}
        required={required}
      />
      {isInTheList && (
        <Typography id={helperTextId} variant="subtitle1" color="textSecondary">
          <FormattedMessage {...conditionMessages.isInTheListHelperText} />
        </Typography>
      )}
      <Error error={errorMessage} align="right" />
    </>
  );
};
